import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import { createPatient, fetchPatients } from '../../store/slices/patientsSlice';
import Swal from 'sweetalert2';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'remixicon/fonts/remixicon.css';

const AddPatient = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error } = useSelector((state) => state.patients);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    dob: '',
    gender: '',
    cnic: '',
    phone: '',
    email: '',
    address: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    status: 1,
  });

  const [formErrors, setFormErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;

    // --- 1. CNIC Masking (xxxxx-xxxxxxx-x) ---
    if (name === "cnic") {
      // Remove all non-digits and limit to 13 digits total
      const digits = value.replace(/\D/g, "").slice(0, 13);

      // Construct the mask based on length
      if (digits.length <= 5) {
        finalValue = digits;
      } else if (digits.length <= 12) {
        finalValue = `${digits.slice(0, 5)}-${digits.slice(5)}`;
      } else {
        finalValue = `${digits.slice(0, 5)}-${digits.slice(5, 12)}-${digits.slice(12, 13)}`;
      }
    }

    // --- 2. Phone Masking (xxxx-xxxxxxx) ---
    // Applies to both 'phone' and 'emergency_contact_phone'
    if (name === "phone" || name === "emergency_contact_phone") {
      // Remove all non-digits and limit to 11 digits total
      const digits = value.replace(/\D/g, "").slice(0, 11);

      // Construct the mask
      if (digits.length <= 4) {
        finalValue = digits;
      } else {
        finalValue = `${digits.slice(0, 4)}-${digits.slice(4)}`;
      }
    }

    // --- 3. Update State ---
    setFormData((prev) => ({
      ...prev,
      [name]: finalValue,
    }));

    // --- 4. Inline Validation (Email) ---
    if (name === "email") {
      let errorMsg = "";
      if (finalValue && !finalValue.includes("@")) {
        errorMsg = "Email must contain '@'";
      }
      setFormErrors((prev) => ({
        ...prev,
        email: errorMsg,
      }));
    }

    // --- 5. Clear specific field errors on change (Optional but recommended) ---
    // This removes the red border immediately when the user starts correcting the CNIC/Phone
    if (formErrors[name] && name !== 'email') {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.first_name.trim()) errors.first_name = 'First name is required';
    if (!formData.dob) errors.dob = 'Date of birth is required';
    if (!formData.gender) errors.gender = 'Gender is required';
    if (!formData.cnic.trim()) errors.cnic = 'CNIC is required';
    if (!formData.phone.trim()) errors.phone = 'Phone is required';
    if (!formData.emergency_contact_name.trim()) errors.emergency_contact_name = 'Emergency contact name is required';
    if (!formData.emergency_contact_phone.trim()) errors.emergency_contact_phone = 'Emergency contact phone is required';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Enter a valid email';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await dispatch(createPatient(formData)).unwrap();
      await dispatch(fetchPatients());

      // ✅ Show SweetAlert and then navigate
      Swal.fire({
        title: 'Success!',
        text: 'Patient created successfully!',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
        position: 'top',
        width: '300px',
      }).then(() => {
        navigate('/patients');
      });
    } catch (err) {
      setFormErrors({ general: err.message || 'Failed to create patient' });
      Swal.fire({
        title: 'Error!',
        text: err.message || 'Failed to create patient.',
        icon: 'error',
        confirmButtonText: 'OK',
        position: 'top',
      });
    }
  };

  return (
    <MainLayout>
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="card mb-4">
          <div className="card-header d-flex justify-content-center align-items-center">
            <h4 className="mb-0">Add Active Client</h4>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit} id="addPatientForm">
              {formErrors.general && <div className="alert alert-danger">{formErrors.general}</div>}

              {/* ===== Patient Info ===== */}
              <div className="row">
                <div className="col-md-3 mb-3">
                  <div className="form-floating form-floating-outline">
                    <input
                      type="text"
                      className={`form-control ${formErrors.first_name ? 'is-invalid' : ''}`}
                      id="first_name"
                      name="first_name"
                      placeholder="First Name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="first_name">First Name</label>
                    {formErrors.first_name && <div className="invalid-feedback">{formErrors.first_name}</div>}
                  </div>
                </div>

                <div className="col-md-3 mb-3">
                  <div className="form-floating form-floating-outline">
                    <input
                      type="text"
                      className="form-control"
                      id="last_name"
                      name="last_name"
                      placeholder="Last Name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="last_name">Last Name</label>
                  </div>
                </div>

                <div className="col-md-3 mb-3">
                  <div className="form-floating form-floating-outline">
                    <input
                      type="date"
                      className={`form-control ${formErrors.dob ? 'is-invalid' : ''}`}
                      id="dob"
                      name="dob"
                      value={formData.dob}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="dob">Date of Birth</label>
                    {formErrors.dob && <div className="invalid-feedback">{formErrors.dob}</div>}
                  </div>
                </div>

                <div className="col-md-3 mb-3">
                  <div className="form-floating form-floating-outline">
                    <select
                      className={`form-select ${formErrors.gender ? 'is-invalid' : ''}`}
                      id="gender"
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                    <label htmlFor="gender">Gender</label>
                    {formErrors.gender && <div className="invalid-feedback">{formErrors.gender}</div>}
                  </div>
                </div>
              </div>

              {/* ===== Contact Info ===== */}
              <div className="row">
                <div className="col-md-3 mb-3">
                  <div className="form-floating form-floating-outline">
                    <input
                      type="text"
                      className={`form-control ${formErrors.cnic ? 'is-invalid' : ''}`}
                      id="cnic"
                      name="cnic"
                      placeholder="CNIC"
                      value={formData.cnic}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="cnic">CNIC</label>
                    {formErrors.cnic && <div className="invalid-feedback">{formErrors.cnic}</div>}
                  </div>
                </div>

                <div className="col-md-3 mb-3">
                  <div className="form-floating form-floating-outline">
                    <input
                      type="tel"
                      className={`form-control ${formErrors.phone ? 'is-invalid' : ''}`}
                      id="phone"
                      name="phone"
                      placeholder="Phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="phone">Phone</label>
                    {formErrors.phone && <div className="invalid-feedback">{formErrors.phone}</div>}
                  </div>
                </div>

                <div className="col-md-3 mb-3">
                  <div className="form-floating form-floating-outline">
                    <input
                      type="email"
                      className={`form-control ${formErrors.email ? 'is-invalid' : ''}`}
                      id="email"
                      name="email"
                      placeholder="Email"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="email">Email</label>
                    {formErrors.email && <div className="invalid-feedback">{formErrors.email}</div>}
                  </div>
                </div>

                <div className="col-md-3 mb-3">
                  <div className="form-floating form-floating-outline">
                    <select
                      className="form-select"
                      id="status"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                    >
                      <option value="0">Inactive</option>
                      <option value="1">Active</option>
                      <option value="2">Blocked</option>
                    </select>
                    <label htmlFor="status">Status</label>
                  </div>
                </div>
              </div>


              <div className="row">
                <div className="col-md-6 mb-3">
                  <div className="form-floating form-floating-outline">
                    <input
                      type="text"
                      className="form-control"
                      id="address"
                      name="address"
                      placeholder="Address"
                      value={formData.address}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="address">Address</label>
                  </div>
                </div>

                {/* ===== Emergency Contact ===== */}

                <div className="col-md-3 mb-3">
                  <div className="form-floating form-floating-outline">
                    <input
                      type="text"
                      className={`form-control ${formErrors.emergency_contact_name ? 'is-invalid' : ''}`}
                      id="emergency_contact_name"
                      name="emergency_contact_name"
                      placeholder="Emergency Contact Name"
                      value={formData.emergency_contact_name}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="emergency_contact_name">Emergency Contact Name</label>
                    {formErrors.emergency_contact_name && (
                      <div className="invalid-feedback">{formErrors.emergency_contact_name}</div>
                    )}
                  </div>
                </div>

                <div className="col-md-3 mb-3">
                  <div className="form-floating form-floating-outline">
                    <input
                      type="tel"
                      className={`form-control ${formErrors.emergency_contact_phone ? 'is-invalid' : ''}`}
                      id="emergency_contact_phone"
                      name="emergency_contact_phone"
                      placeholder="Emergency Contact Phone"
                      value={formData.emergency_contact_phone}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="emergency_contact_phone">Emergency Contact Phone</label>
                    {formErrors.emergency_contact_phone && (
                      <div className="invalid-feedback">{formErrors.emergency_contact_phone}</div>
                    )}
                  </div>
                </div>

              </div>

              {/* ===== Submit Buttons ===== */}
              <div className="row mt-5 pt-3 border-top">
                <div className="col-1 pe-0">
                  <button
                    type="button"
                    className="btn btn-outline-secondary w-100 btn-sm"
                    onClick={() => navigate('/patients')}
                  >
                    <i className="ri-arrow-left-line me-1"></i>Back
                  </button>
                </div>
                <div className="col-2">
                  <button
                    type="submit"
                    className="btn btn-primary w-100 btn-sm"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                        Create
                      </>
                    ) : (
                      <>
                        <i className="ri-user-add-line me-1"></i>Create
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div >
    </MainLayout >
  );
};

export default AddPatient;
